/* eslint-disable react/forbid-prop-types */
/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogFooter, DialogType,
  PrimaryButton, DefaultButton,
  Link,
  IDialogContentProps,
  IModalProps,
} from '@fluentui/react';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { getStyles } from './confirm-dialog-styles';
import { mobileDevice } from '../use-media-query';

export interface ConfirmDialogProps {
  hidden: boolean;
  title?: string | React.ReactElement;
  onConfirm: React.MouseEventHandler<HTMLButtonElement>;
  confirmText?: string;
  isConfirmButtonDisabled?: boolean;
  secondaryButtons?: React.ReactElement[];
  hideCancelButton?: boolean;
  cancelText?: string;
  onCancel: React.MouseEventHandler<HTMLButtonElement>;
  helpLinkText?: string;
  onHelpLink?: React.MouseEventHandler<HTMLElement>;
  children?: React.ReactNode;
  className?: string;
  onDismiss?: () => void;
  dialogContentProps?: IDialogContentProps;
  modalProps?: IModalProps;
  minWidth?: number;
  mobileDeviceMediaQuery?: string;
}

export const ConfirmDialog = (props: ConfirmDialogProps) => {
  const i18n = useContext(I18nContext);

  const {
    hidden,
    title,

    onConfirm,
    confirmText,
    isConfirmButtonDisabled,

    secondaryButtons,

    hideCancelButton,
    cancelText,
    onCancel,

    helpLinkText,
    onHelpLink,

    children,
    className,
    onDismiss,
    dialogContentProps,
    modalProps,
    minWidth,
    mobileDeviceMediaQuery,
  } = props;

  const classes = useDefaultStyles(() => getStyles(mobileDeviceMediaQuery));

  return (
    <Dialog
      hidden={hidden}
      styles={{ main: classes.confirmDialog }}
      closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
      // onDismiss is used to control the top-right close button and light dismiss
      // if onDismiss parameter is not provided, keep same behavior as cancel button
      onDismiss={onDismiss || onCancel}
      minWidth={minWidth || 500}
      dialogContentProps={{
        showCloseButton: true,
        type: DialogType.normal,
        ...dialogContentProps,
      }}
      title={title}
      modalProps={{
        className,
        ...modalProps,
      }}
    >
      {children}
      <DialogFooter>
        <PrimaryButton
          autoFocus
          className="dialog-confirm"
          onClick={onConfirm}
          text={confirmText}
          disabled={isConfirmButtonDisabled}
        />
        {secondaryButtons}
        {
          !hideCancelButton && <DefaultButton
            className="dialog-cancel"
            onClick={onCancel}
            text={cancelText || i18n.getString(_TL_('Cancel'))}
          />
        }
        {
          onHelpLink && (
            <Link
              className="dialog-help"
              onClick={onHelpLink}
            >
              {helpLinkText}
            </Link>
          )
        }
      </DialogFooter>
    </Dialog>
  );
};

ConfirmDialog.propTypes = {
  hidden: PropTypes.bool.isRequired,
  title: PropTypes.node,

  onConfirm: PropTypes.func.isRequired,
  confirmText: PropTypes.string,
  isConfirmButtonDisabled: PropTypes.bool,

  secondaryButtons: PropTypes.arrayOf(PropTypes.element),

  hideCancelButton: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  cancelText: PropTypes.string,

  onHelpLink: PropTypes.func,
  helpLinkText: PropTypes.string,

  children: PropTypes.node,
  className: PropTypes.string,
  onDismiss: PropTypes.func,
  dialogContentProps: PropTypes.object,
  modalProps: PropTypes.object,
  minWidth: PropTypes.number,
  mobileDeviceMediaQuery: PropTypes.string,
};

ConfirmDialog.defaultProps = {
  secondaryButtons: null,
  hideCancelButton: false,
  confirmText: null,
  isConfirmButtonDisabled: false,
  cancelText: null,
  children: null,
  title: null,

  onHelpLink: null,
  helpLinkText: null,
  className: null,
  onDismiss: null,
  dialogContentProps: {},
  modalProps: {},
  minWidth: null,
  mobileDeviceMediaQuery: mobileDevice,
};
